import { Injectable, computed, signal } from '@angular/core';
import { Announcement } from './announcement.model';

const CLOSED_STORAGE_KEY = 'announcements_closed';

/**
 * The announcements the banner shows. Any part of the app may show or remove one.
 *
 * An announcement the user closed stays hidden in this tab, also after a reload (kept in
 * `sessionStorage` by its id); showing it again does not bring it back. Give a changed message
 * a new id.
 */
@Injectable({ providedIn: 'root' })
export class AnnouncementService {
  private readonly announcements = signal<Announcement[]>([]);
  private readonly closedIds = signal<string[]>(readClosedIds());

  /** The announcements to show, oldest first. */
  readonly visible = computed(() => {
    const closedIds = this.closedIds();
    return this.announcements().filter((announcement) => !closedIds.includes(announcement.id));
  });

  /** Shows an announcement, or replaces the one with the same id. */
  show(announcement: Announcement): void {
    this.announcements.update((announcements) => [
      ...announcements.filter((existing) => existing.id !== announcement.id),
      announcement,
    ]);
  }

  remove(id: string): void {
    this.announcements.update((announcements) => announcements.filter((announcement) => announcement.id !== id));
  }

  /** Hides an announcement for the rest of this tab's life, as the user asked. */
  close(id: string): void {
    const closedIds = [...this.closedIds().filter((closedId) => closedId !== id), id];
    this.closedIds.set(closedIds);
    try {
      sessionStorage.setItem(CLOSED_STORAGE_KEY, JSON.stringify(closedIds));
    } catch {
      // Hidden in this page only
    }
  }
}

function readClosedIds(): string[] {
  try {
    const closedIds: unknown = JSON.parse(sessionStorage.getItem(CLOSED_STORAGE_KEY) ?? '[]');
    return Array.isArray(closedIds) ? closedIds.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    return [];
  }
}
